import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { FlagsProvider } from '@atlaskit/flag';
import { Inline, Text } from '@atlaskit/primitives/compiled';

import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics';
import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';

import {
	SmartsFeedbackComponent,
	FeedbackCollectorPropertiesProvider,
} from '@confluence/quick-summary';

import type { ActiveAudioContent } from '../../types/activeAudioContent';

const ANALYTICS_SOURCE = 'aiAudio';
const AUDIO_FEEDBACK_ENTRYPOINT_ID = 'cca5a6aa-d4e5-4195-8927-eb10c3620ff8';

const i18n = defineMessages({
	qualityDisclaimer: {
		id: 'audio.audio-briefing-popup.feedback-footer.quality-disclaimer',
		defaultMessage: 'Uses AI. Quality may vary.',
		description:
			'A disclaimer in the footer of our audio player warning that AI generated results may vary.',
	},
});

const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
	product: 'confluence',
	subproduct: 'confluence',
	source: 'page',
	aiFeatureName: 'aiAudio',
	proactiveGeneratedAI: 0,
	userGeneratedAI: 1,
};

export type AudioBriefingPopupFeedbackFooterProps = {
	activeContent?: ActiveAudioContent | undefined;
};

export const AudioBriefingPopupFeedbackFooter = ({
	activeContent,
}: AudioBriefingPopupFeedbackFooterProps) => {
	return (
		<FlagsProvider>
			<FeedbackCollectorPropertiesProvider entrypointId={AUDIO_FEEDBACK_ENTRYPOINT_ID}>
				<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
					<Inline spread="space-between" alignBlock="center">
						<Text size="small" color="color.text.subtlest">
							<FormattedMessage {...i18n.qualityDisclaimer} />
						</Text>
						<SmartsFeedbackComponent
							contentId={activeContent?.id ?? ''}
							analyticsProperties={{
								source: ANALYTICS_SOURCE,
								additionalAnalyticsAttributes: {
									contentType: activeContent?.contentType ?? '',
								},
							}}
						/>
					</Inline>
				</AIEventsInstrumentationProvider>
			</FeedbackCollectorPropertiesProvider>
		</FlagsProvider>
	);
};
